import React, { useRef, useEffect, useState, useMemo, memo } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { motion, useInView, useReducedMotion } from 'framer-motion';
import { cn } from '@/lib/utils';
import {
  easings,
  durations,
  staggers,
  heroTextVariants,
  heroWordVariants,
  charStaggerVariants,
  charVariants,
  sectionRevealVariants,
  staggerContainerVariants,
  cardRevealVariants,
  cardHoverVariants,
  buttonVariants,
  fadeUpVariants,
  scaleInVariants,
  gsapDefaults,
} from '@/lib/animationConfig';

// Register GSAP plugins once
gsap.registerPlugin(ScrollTrigger);

// =========================================
// PERFORMANCE: Check for reduced motion
// =========================================
const useOptimizedAnimation = () => {
  const prefersReducedMotion = useReducedMotion();
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  return { prefersReducedMotion, isMobile };
};

// =========================================
// HERO TEXT WITH WORD STAGGER
// GPU-optimized with will-change
// =========================================
export const HeroText = memo(({ 
  children, 
  className,
  delay = 0,
  as: Component = 'h1'
}) => {
  const { prefersReducedMotion, isMobile } = useOptimizedAnimation();
  const words = typeof children === 'string' ? children.split(' ') : [];

  if (prefersReducedMotion) {
    return <Component className={className}>{children}</Component>;
  }

  const variants = isMobile 
    ? {
        ...heroTextVariants,
        visible: {
          ...heroTextVariants.visible,
          transition: { staggerChildren: 0.03, delayChildren: delay },
        },
      }
    : {
        ...heroTextVariants,
        visible: {
          ...heroTextVariants.visible,
          transition: { ...heroTextVariants.visible.transition, delayChildren: delay },
        },
      };

  return (
    <Component className={className}>
      <motion.span
        initial="hidden"
        animate="visible"
        variants={variants}
        className="inline"
      >
        {words.map((word, i) => (
          <motion.span
            key={i}
            variants={heroWordVariants}
            className="inline-block mr-[0.25em] will-change-transform"
            style={{ willChange: 'transform, opacity' }}
          >
            {word}
          </motion.span>
        ))}
      </motion.span>
    </Component>
  );
});

HeroText.displayName = 'HeroText';

// =========================================
// CHARACTER STAGGER TEXT
// For special emphasis headings
// =========================================
export const CharacterText = memo(({ 
  children, 
  className,
  delay = 0 
}) => {
  const { prefersReducedMotion } = useOptimizedAnimation();
  const chars = typeof children === 'string' ? children.split('') : [];

  if (prefersReducedMotion) {
    return <span className={className}>{children}</span>;
  }

  return (
    <motion.span
      initial="hidden"
      animate="visible"
      variants={{
        ...charStaggerVariants,
        visible: {
          ...charStaggerVariants.visible,
          transition: { ...charStaggerVariants.visible.transition, delayChildren: delay },
        },
      }}
      className={cn('inline-block', className)}
    >
      {chars.map((char, i) => (
        <motion.span
          key={i}
          variants={charVariants}
          className="inline-block will-change-transform"
          style={{ willChange: 'transform, opacity' }}
        >
          {char === ' ' ? '\u00A0' : char}
        </motion.span>
      ))}
    </motion.span>
  );
});

CharacterText.displayName = 'CharacterText';

// =========================================
// SCROLL REVEAL SECTION
// Uses IntersectionObserver (GPU-friendly)
// =========================================
export const ScrollReveal = memo(({ 
  children, 
  className,
  delay = 0,
  direction = 'up', // 'up', 'down', 'left', 'right'
  distance = 40,
  once = true,
}) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once, margin: '-100px' });
  const { prefersReducedMotion, isMobile } = useOptimizedAnimation();

  const getInitialProps = () => {
    if (prefersReducedMotion) return { opacity: 0 };
    
    const actualDistance = isMobile ? distance * 0.5 : distance;
    
    switch (direction) {
      case 'down': return { opacity: 0, y: -actualDistance };
      case 'left': return { opacity: 0, x: actualDistance };
      case 'right': return { opacity: 0, x: -actualDistance };
      default: return { opacity: 0, y: actualDistance };
    }
  };

  return (
    <motion.div
      ref={ref}
      initial={getInitialProps()}
      animate={isInView ? { opacity: 1, x: 0, y: 0 } : getInitialProps()}
      transition={{
        duration: prefersReducedMotion ? 0.01 : (isMobile ? 0.4 : 0.6),
        delay: prefersReducedMotion ? 0 : delay,
        ease: easings.easeOut,
      }}
      className={cn('will-change-transform', className)}
      style={{ willChange: 'transform, opacity' }}
    >
      {children}
    </motion.div>
  );
});

ScrollReveal.displayName = 'ScrollReveal';

// =========================================
// STAGGERED CONTAINER
// For card grids with staggered reveal
// =========================================
export const StaggerContainer = memo(({ 
  children, 
  className,
  staggerDelay = 0.08,
  once = true,
}) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once, margin: '-50px' });
  const { prefersReducedMotion, isMobile } = useOptimizedAnimation();

  const actualStagger = prefersReducedMotion ? 0 : (isMobile ? staggerDelay * 0.5 : staggerDelay);

  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={isInView ? 'visible' : 'hidden'}
      variants={{
        hidden: { opacity: 0 },
        visible: {
          opacity: 1,
          transition: {
            staggerChildren: actualStagger,
            delayChildren: 0.1,
          },
        },
      }}
      className={className}
    >
      {children}
    </motion.div>
  );
});

StaggerContainer.displayName = 'StaggerContainer';

// =========================================
// STAGGER ITEM
// Individual item in staggered container
// =========================================
export const StaggerItem = memo(({ children, className }) => {
  const { prefersReducedMotion, isMobile } = useOptimizedAnimation();

  return (
    <motion.div
      variants={{
        hidden: { 
          opacity: 0, 
          y: prefersReducedMotion ? 0 : (isMobile ? 15 : 30),
          scale: prefersReducedMotion ? 1 : 0.95,
        },
        visible: { 
          opacity: 1, 
          y: 0,
          scale: 1,
          transition: {
            duration: prefersReducedMotion ? 0.01 : (isMobile ? 0.3 : 0.5),
            ease: easings.easeOut,
          },
        },
      }}
      className={cn('will-change-transform', className)}
      style={{ willChange: 'transform, opacity' }}
    >
      {children}
    </motion.div>
  );
});

StaggerItem.displayName = 'StaggerItem';

// =========================================
// INTERACTIVE CARD
// With optimized hover/tap states
// =========================================
export const InteractiveCard = memo(({ 
  children, 
  className,
  hoverScale = 1.03,
  hoverY = -8,
  glowColor = 'rgba(0, 137, 123, 0.3)',
}) => {
  const { prefersReducedMotion } = useOptimizedAnimation();

  if (prefersReducedMotion) {
    return <div className={className}>{children}</div>;
  }

  return (
    <motion.div
      initial="rest"
      whileHover="hover"
      whileTap="tap"
      variants={{
        rest: { 
          scale: 1, 
          y: 0,
          boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
        },
        hover: { 
          scale: hoverScale, 
          y: hoverY,
          boxShadow: `0 20px 40px rgba(0,0,0,0.3), 0 0 30px ${glowColor}`,
          transition: { duration: 0.2, ease: easings.easeOut },
        },
        tap: { 
          scale: 0.98,
          transition: { duration: 0.1 },
        },
      }}
      className={cn('will-change-transform cursor-pointer', className)}
      style={{ willChange: 'transform, box-shadow' }}
    >
      {children}
    </motion.div>
  );
});

InteractiveCard.displayName = 'InteractiveCard';

// =========================================
// MAGNETIC BUTTON (Optimized)
// Subtle magnetic pull effect
// =========================================
export const MagneticButton = memo(({ 
  children, 
  className,
  strength = 0.3,
}) => {
  const ref = useRef(null);
  const { prefersReducedMotion } = useOptimizedAnimation();

  useEffect(() => {
    if (prefersReducedMotion || !ref.current) return;

    const el = ref.current;
    let rafId = null;

    const handleMouseMove = (e) => {
      if (rafId) cancelAnimationFrame(rafId);
      
      rafId = requestAnimationFrame(() => {
        const rect = el.getBoundingClientRect();
        const x = (e.clientX - rect.left - rect.width / 2) * strength;
        const y = (e.clientY - rect.top - rect.height / 2) * strength;
        
        el.style.transform = `translate(${x}px, ${y}px)`;
      });
    };

    const handleMouseLeave = () => {
      if (rafId) cancelAnimationFrame(rafId);
      el.style.transform = 'translate(0, 0)';
      el.style.transition = 'transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)';
      setTimeout(() => {
        el.style.transition = '';
      }, 300);
    };

    el.addEventListener('mousemove', handleMouseMove);
    el.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      el.removeEventListener('mousemove', handleMouseMove);
      el.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [strength, prefersReducedMotion]);

  return (
    <div 
      ref={ref} 
      className={cn('inline-block will-change-transform', className)}
      style={{ willChange: 'transform' }}
    >
      <motion.div
        whileHover={{ scale: prefersReducedMotion ? 1 : 1.05 }}
        whileTap={{ scale: prefersReducedMotion ? 1 : 0.95 }}
        transition={{ duration: 0.15 }}
      >
        {children}
      </motion.div>
    </div>
  );
});

MagneticButton.displayName = 'MagneticButton';

// =========================================
// FLOATING ELEMENT
// CSS animation for better performance
// =========================================
export const FloatingElement = memo(({ 
  children, 
  className,
  duration = 6,
  distance = 15,
  delay = 0,
}) => {
  const { prefersReducedMotion } = useOptimizedAnimation();

  if (prefersReducedMotion) {
    return <div className={className}>{children}</div>;
  }

  return (
    <motion.div
      animate={{
        y: [-distance, distance, -distance],
      }}
      transition={{
        duration,
        repeat: Infinity,
        ease: 'easeInOut',
        delay,
      }}
      className={cn('will-change-transform', className)}
      style={{ willChange: 'transform' }}
    >
      {children}
    </motion.div>
  );
});

FloatingElement.displayName = 'FloatingElement';

// =========================================
// GSAP SCROLL REVEAL
// For complex scroll-based animations
// =========================================
export const GSAPScrollReveal = memo(({ 
  children, 
  className,
  animation = 'fadeUp', // 'fadeUp', 'scaleIn', 'slideIn'
  delay = 0,
  duration = 0.6,
}) => {
  const ref = useRef(null);
  const { prefersReducedMotion, isMobile } = useOptimizedAnimation();

  useEffect(() => {
    if (prefersReducedMotion || !ref.current) return;

    const animConfig = gsapDefaults[animation] || gsapDefaults.fadeUp;
    const actualDuration = isMobile ? duration * 0.7 : duration;

    const ctx = gsap.context(() => {
      gsap.fromTo(
        ref.current,
        { ...animConfig.from, willChange: 'transform, opacity' },
        {
          ...animConfig.to,
          duration: actualDuration,
          delay,
          scrollTrigger: {
            trigger: ref.current,
            ...gsapDefaults.scrollTrigger,
          },
          clearProps: 'willChange', // Clean up after animation
        }
      );
    }, ref);

    return () => ctx.revert();
  }, [animation, delay, duration, prefersReducedMotion, isMobile]);

  return (
    <div ref={ref} className={className}>
      {children}
    </div>
  );
});

GSAPScrollReveal.displayName = 'GSAPScrollReveal';

// =========================================
// GSAP COUNTER (Optimized)
// Number counting animation
// =========================================
export const GSAPCounter = memo(({ 
  value, 
  suffix = '', 
  prefix = '',
  duration = 2,
  className,
}) => {
  const ref = useRef(null);
  const counterRef = useRef({ value: 0 });
  const { prefersReducedMotion } = useOptimizedAnimation();

  useEffect(() => {
    if (!ref.current) return;

    const numericValue = parseFloat(String(value).replace(/[^\d.]/g, ''));
    
    if (prefersReducedMotion) {
      ref.current.textContent = `${prefix}${numericValue.toLocaleString()}${suffix}`;
      return;
    }

    const ctx = gsap.context(() => {
      gsap.to(counterRef.current, {
        value: numericValue,
        duration,
        ease: 'power2.out',
        scrollTrigger: {
          trigger: ref.current,
          start: 'top 85%',
          toggleActions: 'play none none none',
        },
        onUpdate: () => {
          if (ref.current) {
            const formatted = Math.round(counterRef.current.value).toLocaleString();
            ref.current.textContent = `${prefix}${formatted}${suffix}`;
          }
        },
      });
    }, ref);

    return () => ctx.revert();
  }, [value, suffix, prefix, duration, prefersReducedMotion]);

  return (
    <span ref={ref} className={className}>
      {prefix}0{suffix}
    </span>
  );
});

GSAPCounter.displayName = 'GSAPCounter';

// =========================================
// NEON GLOW LINE (Optimized SVG)
// Uses CSS animation instead of GSAP
// =========================================
export const NeonGlowLine = memo(({ className }) => {
  const { prefersReducedMotion } = useOptimizedAnimation();

  return (
    <svg 
      viewBox="0 0 1200 120" 
      className={cn('w-full h-auto overflow-visible', className)}
      preserveAspectRatio="none"
    >
      <defs>
        <linearGradient id="neonGradient" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="hsl(174, 100%, 27%)" stopOpacity="0" />
          <stop offset="50%" stopColor="hsl(174, 100%, 50%)" stopOpacity="1" />
          <stop offset="100%" stopColor="hsl(174, 100%, 27%)" stopOpacity="0" />
        </linearGradient>
        <filter id="glow">
          <feGaussianBlur stdDeviation="4" result="coloredBlur"/>
          <feMerge>
            <feMergeNode in="coloredBlur"/>
            <feMergeNode in="SourceGraphic"/>
          </feMerge>
        </filter>
      </defs>
      
      {/* Glow layer */}
      <path
        d="M 0 80 Q 300 20, 600 60 T 1200 40"
        fill="none"
        stroke="url(#neonGradient)"
        strokeWidth="4"
        filter="url(#glow)"
        opacity="0.6"
        className={prefersReducedMotion ? '' : 'animate-pulse'}
      />
      
      {/* Main line */}
      <path
        d="M 0 80 Q 300 20, 600 60 T 1200 40"
        fill="none"
        stroke="url(#neonGradient)"
        strokeWidth="2"
        className={prefersReducedMotion ? '' : 'neon-line-draw'}
      />
      
      <style>{`
        .neon-line-draw {
          stroke-dasharray: 1200;
          stroke-dashoffset: 1200;
          animation: drawLine 2s ease-out forwards;
        }
        @keyframes drawLine {
          to {
            stroke-dashoffset: 0;
          }
        }
      `}</style>
    </svg>
  );
});

NeonGlowLine.displayName = 'NeonGlowLine';

// =========================================
// CURVED SECTION DIVIDER
// SVG wave divider between sections
// =========================================
export const CurvedDivider = memo(({ 
  className,
  fill = 'hsl(215, 50%, 8%)',
  flip = false,
}) => {
  return (
    <div className={cn('w-full overflow-hidden', flip && 'rotate-180', className)}>
      <svg 
        viewBox="0 0 1440 120" 
        className="w-full h-auto"
        preserveAspectRatio="none"
      >
        <path
          d="M0,60 C360,120 720,0 1080,60 C1260,90 1380,30 1440,60 L1440,120 L0,120 Z"
          fill={fill}
        />
      </svg>
    </div>
  );
});

CurvedDivider.displayName = 'CurvedDivider';

// =========================================
// PARALLAX BACKGROUND ELEMENT
// Uses transform only (GPU optimized)
// =========================================
export const ParallaxBG = memo(({ 
  children, 
  className,
  speed = 0.3,
}) => {
  const ref = useRef(null);
  const { prefersReducedMotion, isMobile } = useOptimizedAnimation();

  useEffect(() => {
    if (prefersReducedMotion || isMobile || !ref.current) return;

    const ctx = gsap.context(() => {
      gsap.to(ref.current, {
        y: speed * 100,
        ease: 'none',
        scrollTrigger: {
          trigger: ref.current,
          start: 'top bottom',
          end: 'bottom top',
          scrub: 0.5, // Smooth scrubbing
        },
      });
    }, ref);

    return () => ctx.revert();
  }, [speed, prefersReducedMotion, isMobile]);

  return (
    <div 
      ref={ref} 
      className={cn('will-change-transform', className)}
      style={{ willChange: 'transform' }}
    >
      {children}
    </div>
  );
});

ParallaxBG.displayName = 'ParallaxBG';

// =========================================
// TEXT SCRAMBLE EFFECT (Optimized)
// Matrix-style text scramble on scroll
// =========================================
export const TextScramble = memo(({ 
  text, 
  className,
  trigger = 'scroll', // 'scroll' or 'hover'
}) => {
  const ref = useRef(null);
  const [displayText, setDisplayText] = useState(text);
  const { prefersReducedMotion } = useOptimizedAnimation();
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';

  useEffect(() => {
    if (prefersReducedMotion || !ref.current) {
      setDisplayText(text);
      return;
    }

    const scramble = () => {
      let iteration = 0;
      const interval = setInterval(() => {
        setDisplayText(
          text.split('').map((char, index) => {
            if (index < iteration) return text[index];
            if (char === ' ') return ' ';
            return chars[Math.floor(Math.random() * chars.length)];
          }).join('')
        );

        if (iteration >= text.length) {
          clearInterval(interval);
        }
        iteration += 1 / 2;
      }, 40);
    };

    if (trigger === 'scroll') {
      ScrollTrigger.create({
        trigger: ref.current,
        start: 'top 85%',
        onEnter: scramble,
        once: true,
      });
    } else {
      const el = ref.current;
      el.addEventListener('mouseenter', scramble);
      return () => el?.removeEventListener('mouseenter', scramble);
    }
  }, [text, trigger, prefersReducedMotion]);

  return (
    <span ref={ref} className={cn('font-mono', className)}>
      {displayText}
    </span>
  );
});

TextScramble.displayName = 'TextScramble';

// =========================================
// MARQUEE (Optimized with CSS animation)
// Smooth infinite scroll
// =========================================
export const OptimizedMarquee = memo(({ 
  children, 
  className,
  speed = 40, // pixels per second
  direction = 'left',
  pauseOnHover = true,
}) => {
  const { prefersReducedMotion } = useOptimizedAnimation();
  
  const animationDuration = prefersReducedMotion ? 0 : (1920 / speed);

  return (
    <div 
      className={cn(
        'overflow-hidden whitespace-nowrap',
        pauseOnHover && 'hover:[&_.marquee-track]:pause-animation',
        className
      )}
    >
      <div 
        className="marquee-track inline-flex will-change-transform"
        style={{
          animation: prefersReducedMotion 
            ? 'none' 
            : `marquee-${direction} ${animationDuration}s linear infinite`,
          willChange: 'transform',
        }}
      >
        {children}
        {children}
      </div>
      
      <style>{`
        @keyframes marquee-left {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        @keyframes marquee-right {
          0% { transform: translateX(-50%); }
          100% { transform: translateX(0); }
        }
        .pause-animation {
          animation-play-state: paused !important;
        }
      `}</style>
    </div>
  );
});

OptimizedMarquee.displayName = 'OptimizedMarquee';

export default {
  HeroText,
  CharacterText,
  ScrollReveal,
  StaggerContainer,
  StaggerItem,
  InteractiveCard,
  MagneticButton,
  FloatingElement,
  GSAPScrollReveal,
  GSAPCounter,
  NeonGlowLine,
  CurvedDivider,
  ParallaxBG,
  TextScramble,
  OptimizedMarquee,
};
